import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Facebook, Instagram, Twitter, Youtube, Linkedin, Github, Twitch,
  Send, MessageCircle, Music2, Apple, Play, Globe,
} from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";

// Single source of truth for the social/app-store platforms the footer supports — shared with the
// admin editor so the dropdown and the rendered icon always agree.
export const SOCIAL_PLATFORMS = [
  { value: "facebook", label: "Facebook", Icon: Facebook },
  { value: "instagram", label: "Instagram", Icon: Instagram },
  { value: "twitter", label: "X / Twitter", Icon: Twitter },
  { value: "youtube", label: "YouTube", Icon: Youtube },
  { value: "tiktok", label: "TikTok", Icon: Music2 },
  { value: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { value: "telegram", label: "Telegram", Icon: Send },
  { value: "twitch", label: "Twitch", Icon: Twitch },
  { value: "github", label: "GitHub", Icon: Github },
  { value: "appstore", label: "Apple App Store", Icon: Apple },
  { value: "playstore", label: "Google Play", Icon: Play },
  { value: "website", label: "Website / other", Icon: Globe },
];
const SOCIAL_MAP = Object.fromEntries(SOCIAL_PLATFORMS.map((p) => [p.value, p]));
export const socialIcon = (platform) => (SOCIAL_MAP[platform] || SOCIAL_MAP.website).Icon;
export const socialLabel = (platform) => (SOCIAL_MAP[platform] || SOCIAL_MAP.website).label;

// The built-in footer we fall back to until an admin customises one in Admin → System → Pages.
const builtIn = (t) => [
  { id: "our-story", label: t("nav.ourStory"), href: "/our-story", roles: [] },
  { id: "cities", label: t("footer.cities"), href: "/cities", roles: [] },
  { id: "contact", label: t("footer.contact"), href: "/contact-us", roles: [] },
  { id: "pricing", label: t("footer.pricing"), href: "/pricing", roles: ["business", "admin"] },
  { id: "terms", label: t("footer.terms"), href: "/terms", roles: [] },
  { id: "privacy", label: t("footer.privacy"), href: "/privacy", roles: [] },
];

const isExternal = (href) => /^(https?:|mailto:|tel:)/i.test(href || "");

export const SiteFooter = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const role = user?.role || "guest";
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get(`/content/footer?lang=${lang}`).then(({ data }) => setCfg(data.data || {})).catch(() => setCfg({}));
  }, [lang]);

  if (cfg === null) return null; // avoid a flash of the wrong footer

  const custom = Array.isArray(cfg.links) && cfg.links.length > 0;
  const links = (custom ? cfg.links : builtIn(t))
    .filter((l) => l && l.href && l.label && l.on !== false)
    .filter((l) => !l.roles || l.roles.length === 0 || l.roles.includes(role));
  const tagline = (custom && cfg.tagline) || t("brand.tagline");
  const socials = (cfg.socials || []).filter((s) => s && s.href && s.on !== false);

  return (
    <footer className="px-4 lg:px-6 py-6 border-t border-border mt-2" data-testid="app-footer">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <span data-testid="footer-tagline">© {new Date().getFullYear()} LytePress · {tagline}</span>
        <nav className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {links.map((l) =>
            isExternal(l.href) ? (
              <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer"
                data-testid={`footer-link-${l.id}`}
                className="hover:text-[#16a34a] transition-colors">{l.label}</a>
            ) : (
              <Link key={l.id} to={l.href} data-testid={`footer-link-${l.id}`}
                className="hover:text-[#16a34a] transition-colors">{l.label}</Link>
            )
          )}
        </nav>
      </div>
      {socials.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-border" data-testid="footer-socials">
          {socials.map((s) => {
            const Icon = socialIcon(s.platform);
            return (
              <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer"
                aria-label={socialLabel(s.platform)} title={socialLabel(s.platform)}
                data-testid={`footer-social-${s.platform}`}
                className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-[#16a34a] hover:border-[#16a34a] transition-colors">
                <Icon size={17} />
              </a>
            );
          })}
        </div>
      )}
    </footer>
  );
};

export default SiteFooter;
